import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { API } from '../App';
import {
  Trophy, TrendingUp, TrendingDown, Target, Calendar, Brain,
  Download, FileText, FileSpreadsheet, Plus, Trash2, RefreshCw,
  AlertTriangle, CheckCircle, Shield, Users, Award, Flame,
  ChevronLeft, ChevronRight, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const SafetyDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [timeRange, setTimeRange] = useState('6months');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [leaderboard, setLeaderboard] = useState([]);
  const [trends, setTrends] = useState(null);
  const [goals, setGoals] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [riskPrediction, setRiskPrediction] = useState(null);
  const [heatmapYear, setHeatmapYear] = useState(new Date().getFullYear());
  
  // Goal creation
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    targetType: 'days_without_recordable',
    targetValue: 90,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 90*24*60*60*1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAllData();
  }, [timeRange]);

  useEffect(() => {
    if (activeTab === 'heatmap') {
      fetchHeatmap();
    }
  }, [activeTab, heatmapYear]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      // Fetch all data in parallel
      const [leaderboardRes, trendsRes, goalsRes, riskRes] = await Promise.all([
        fetch(`${API}/analytics/leaderboard?time_range=${timeRange}`, { headers }),
        fetch(`${API}/analytics/trends?time_range=${timeRange}`, { headers }),
        fetch(`${API}/analytics/goals`, { headers }),
        fetch(`${API}/analytics/risk-prediction`, { headers })
      ]);

      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json();
        setLeaderboard(data.leaderboard || []);
      }

      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrends(data.trends);
      }

      if (goalsRes.ok) {
        const data = await goalsRes.json();
        setGoals(data.goals || []);
      }

      if (riskRes.ok) {
        const data = await riskRes.json();
        setRiskPrediction(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHeatmap = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/analytics/heatmap?year=${heatmapYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHeatmapData(data.data || []);
      }
    } catch (error) {
      console.error('Heatmap error:', error);
    }
  };

  const createGoal = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/analytics/goals`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newGoal)
      });
      if (res.ok) {
        toast.success('Goal created successfully');
        setShowGoalDialog(false);
        fetchAllData();
        setNewGoal({
          title: '',
          targetType: 'days_without_recordable',
          targetValue: 90,
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 90*24*60*60*1000).toISOString().split('T')[0]
        });
      }
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const deleteGoal = async (goalId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/analytics/goals/${goalId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Goal deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const exportReport = async (format) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/analytics/export/${format}?time_range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `safety_report.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        a.click();
        toast.success(`${format.toUpperCase()} report downloaded`);
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Trend indicator component
  const TrendIndicator = ({ trend, inverted = false }) => {
    if (!trend) return null;
    const isPositive = inverted ? trend.change <= 0 : trend.change >= 0;
    const isImproved = trend.improved;
    
    return (
      <div className={`flex items-center gap-1 text-sm ${isImproved ? 'text-green-600' : 'text-red-600'}`}>
        {isImproved ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        <span>{Math.abs(trend.change)}%</span>
      </div>
    );
  };

  // Heat map calendar component
  const HeatMapCalendar = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    
    // Create a map for quick lookup
    const dataMap = {};
    heatmapData.forEach(d => { dataMap[d.date] = d; });
    
    const getSeverityColor = (severity, count) => {
      if (count === 0) return 'bg-gray-100';
      if (severity === 'Critical' || severity === 'High') return 'bg-red-500';
      if (severity === 'Medium') return 'bg-yellow-400';
      return 'bg-green-400';
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setHeatmapYear(heatmapYear - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-lg font-semibold">{heatmapYear}</span>
          <Button variant="outline" size="sm" onClick={() => setHeatmapYear(heatmapYear + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-12 gap-2">
          {months.map((month, monthIdx) => (
            <div key={month} className="space-y-1">
              <div className="text-xs font-medium text-center text-gray-500">{month}</div>
              <div className="grid grid-cols-7 gap-0.5">
                {Array.from({ length: daysInMonth[monthIdx] }, (_, dayIdx) => {
                  const date = `${heatmapYear}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayIdx + 1).padStart(2, '0')}`;
                  const dayData = dataMap[date];
                  
                  return (
                    <div
                      key={date}
                      className={`w-3 h-3 rounded-sm cursor-pointer transition-all hover:scale-150 ${
                        dayData ? getSeverityColor(dayData.severity, dayData.count) : 'bg-gray-100'
                      }`}
                      title={dayData ? `${date}: ${dayData.count} incident(s)` : date}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 justify-center text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-100 rounded-sm"></div>
            <span>None</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
            <span>Low</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-400 rounded-sm"></div>
            <span>Medium</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
            <span>High/Critical</span>
          </div>
        </div>
      </div>
    );
  };

  // Risk gauge component
  const RiskGauge = ({ score, level }) => {
    const getColor = () => {
      if (score < 30) return 'text-green-500';
      if (score < 60) return 'text-yellow-500';
      if (score < 80) return 'text-orange-500';
      return 'text-red-500';
    };

    return (
      <div className="flex flex-col items-center">
        <div className={`text-6xl font-bold ${getColor()}`}>{score}</div>
        <Badge variant={level === 'Low' ? 'secondary' : level === 'Medium' ? 'default' : 'destructive'} className="mt-2">
          {level} Risk
        </Badge>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="mb-2 -ml-2"
              data-testid="safety-dashboard-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Safety Dashboard</h1>
            <p className="text-gray-600">Comprehensive safety analytics & insights</p>
          </div>
          <div className="flex gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30days">30 Days</SelectItem>
                <SelectItem value="6months">6 Months</SelectItem>
                <SelectItem value="1year">1 Year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => exportReport('pdf')}>
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" onClick={() => exportReport('excel')}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        {/* Trend Cards */}
        {trends && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Incidents</p>
                    <p className="text-2xl font-bold">{trends.incidents.current}</p>
                  </div>
                  <TrendIndicator trend={trends.incidents} inverted={true} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">JSAs</p>
                    <p className="text-2xl font-bold">{trends.jsas.current}</p>
                  </div>
                  <TrendIndicator trend={trends.jsas} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Audits</p>
                    <p className="text-2xl font-bold">{trends.audits.current}</p>
                  </div>
                  <TrendIndicator trend={trends.audits} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Near Misses</p>
                    <p className="text-2xl font-bold">{trends.nearMisses.current}</p>
                  </div>
                  <TrendIndicator trend={trends.nearMisses} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="leaderboard" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Leaderboard
            </TabsTrigger>
            <TabsTrigger value="goals" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Goals
            </TabsTrigger>
            <TabsTrigger value="heatmap" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Heat Map
            </TabsTrigger>
            <TabsTrigger value="prediction" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Risk Prediction
            </TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Division Safety Leaderboard
                </CardTitle>
                <CardDescription>Rankings based on safety performance scores</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  </div>
                ) : leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No divisions found</div>
                ) : (
                  <div className="space-y-4">
                    {leaderboard.map((div, idx) => (
                      <div key={div.divisionId} className={`flex items-center p-4 rounded-lg border ${
                        idx === 0 ? 'bg-yellow-50 border-yellow-200' : 
                        idx === 1 ? 'bg-gray-50 border-gray-200' : 
                        idx === 2 ? 'bg-orange-50 border-orange-200' : 'bg-white'
                      }`}>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mr-4 bg-white shadow">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : div.rank}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg">{div.divisionName}</div>
                          <div className="text-sm text-gray-500 flex gap-4">
                            <span>{div.users} workers</span>
                            <span>{div.jsaCount} JSAs</span>
                            <span>{div.totalIncidents} incidents</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-blue-600">{div.safetyScore}</div>
                          <div className="text-sm text-gray-500">Safety Score</div>
                        </div>
                        <div className="ml-6 text-right">
                          <div className="text-xl font-semibold text-green-600">{div.daysSafe}</div>
                          <div className="text-sm text-gray-500">Days Safe</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-500" />
                      Safety Goals
                    </CardTitle>
                    <CardDescription>Track progress toward safety targets</CardDescription>
                  </div>
                  <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Goal
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Safety Goal</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label>Goal Title</Label>
                          <Input 
                            value={newGoal.title} 
                            onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                            placeholder="e.g., 90 Days Without Recordable"
                          />
                        </div>
                        <div>
                          <Label>Goal Type</Label>
                          <Select value={newGoal.targetType} onValueChange={(v) => setNewGoal({...newGoal, targetType: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days_without_recordable">Days Without Recordable</SelectItem>
                              <SelectItem value="audit_score">Average Audit Score (%)</SelectItem>
                              <SelectItem value="jsa_completion">JSA Completion Rate (%)</SelectItem>
                              <SelectItem value="incident_reduction">Max Incidents Allowed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Target Value</Label>
                          <Input 
                            type="number"
                            value={newGoal.targetValue} 
                            onChange={(e) => setNewGoal({...newGoal, targetValue: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Start Date</Label>
                            <Input 
                              type="date"
                              value={newGoal.startDate} 
                              onChange={(e) => setNewGoal({...newGoal, startDate: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>End Date</Label>
                            <Input 
                              type="date"
                              value={newGoal.endDate} 
                              onChange={(e) => setNewGoal({...newGoal, endDate: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowGoalDialog(false)}>Cancel</Button>
                        <Button onClick={createGoal}>Create Goal</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {goals.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No goals set. Click "Add Goal" to create one.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {goals.map((goal) => (
                      <div key={goal.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold">{goal.title}</h4>
                            <p className="text-sm text-gray-500">
                              {goal.startDate} to {goal.endDate}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {goal.progress?.achieved ? (
                              <Badge variant="success" className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Achieved
                              </Badge>
                            ) : (
                              <Badge variant="outline">In Progress</Badge>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => deleteGoal(goal.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Progress: {goal.progress?.currentValue || 0} / {goal.targetValue}</span>
                            <span className="font-semibold">{goal.progress?.percentage || 0}%</span>
                          </div>
                          <Progress value={goal.progress?.percentage || 0} className="h-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Heat Map Tab */}
          <TabsContent value="heatmap" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-500" />
                  Incident Heat Map
                </CardTitle>
                <CardDescription>Visual calendar showing incident frequency and severity</CardDescription>
              </CardHeader>
              <CardContent>
                <HeatMapCalendar />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risk Prediction Tab */}
          <TabsContent value="prediction" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-blue-500" />
                    AI Risk Prediction
                  </CardTitle>
                  <CardDescription>Machine learning powered safety risk assessment</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center py-8">
                  {loading ? (
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                  ) : riskPrediction ? (
                    <>
                      <RiskGauge 
                        score={riskPrediction.prediction.riskScore} 
                        level={riskPrediction.prediction.riskLevel} 
                      />
                      <p className="mt-4 text-center text-gray-600 max-w-md">
                        {riskPrediction.prediction.summary}
                      </p>
                      <div className="mt-4 text-sm text-gray-400">
                        Predicted incident likelihood: <Badge>{riskPrediction.prediction.predictedLikelihood}</Badge>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-500">Unable to generate prediction</p>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {riskPrediction?.prediction.riskFactors.map((factor, idx) => (
                      <div key={idx} className="flex items-center gap-3 py-2 border-b last:border-0">
                        <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </div>
                        <span>{factor}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {riskPrediction?.prediction.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-center gap-3 py-2 border-b last:border-0">
                        <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-bold">
                          ✓
                        </div>
                        <span>{rec}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SafetyDashboard;
